/**
 * @fileoverview useInactivityLogout — 4-hour inactivity safety timeout
 * @module hooks/useInactivityLogout
 *
 * Responsible-use control: signs the user out after a period of inactivity
 * (default 4 hours) so an unattended, still-logged-in session (e.g. left
 * open on a shared family computer) does not stay live indefinitely.
 *
 * Design notes:
 *  - Wall-clock based, not a single long-lived setTimeout. A tab can be
 *    frozen/throttled or the machine can sleep for hours; setTimeout is not
 *    guaranteed to fire promptly (or at all) when that happens. Instead we
 *    record `lastActivityAt` and compare it against Date.now() on a 60s
 *    interval *and* immediately whenever the tab becomes visible/focused
 *    again, which is what actually catches the "laptop slept overnight"
 *    case.
 *  - `lastActivityAt` is mirrored into sessionStorage (throttled) so a
 *    same-tab page refresh does not reset the clock — only genuine new
 *    inactivity should ever start it over. sessionStorage is cleared when
 *    the tab/browser closes, which is consistent with using
 *    browserSessionPersistence for Firebase Auth.
 *  - `visibilitychange` / `focus` / `pageshow` are treated as "please
 *    re-check now", not as activity themselves — otherwise simply
 *    switching back to the tab after a long sleep would silently reset the
 *    timer right before the check ran, and the timeout could never fire.
 *  - On timeout, broadcasts a FORCE_LOGOUT message (BroadcastChannel, with
 *    a localStorage `storage` event fallback) so every open tab signs out
 *    together, and stores a short non-sensitive reason in sessionStorage
 *    that the sign-in page reads to explain why the user landed there.
 */

import { useEffect, useRef, useCallback } from 'react';
import { signOut } from 'firebase/auth';
import { auth } from '../services/firebase';

export const INACTIVITY_LIMIT_MS = 4 * 60 * 60 * 1000; // 4 hours

const CHECK_INTERVAL_MS = 60 * 1000; // re-check every 60 seconds
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];
const BROADCAST_CHANNEL_NAME = 'ss-auth-session';
const STORAGE_FORCE_LOGOUT_KEY = 'ss_force_logout';
const LAST_ACTIVITY_STORAGE_KEY = 'ss_last_activity_at';
const PERSIST_THROTTLE_MS = 3000;

export const LOGOUT_REASON_STORAGE_KEY = 'logoutReason';

// Dev-only debug override so QA can verify the flow without editing source —
// e.g. localStorage.setItem('ss_debug_inactivity_ms', '10000') for a 10s timeout.
const DEBUG_OVERRIDE_KEY = 'ss_debug_inactivity_ms';

function isDev() {
  try {
    return Boolean(import.meta?.env?.DEV);
  } catch {
    return false;
  }
}

function devLog(...args) {
  if (isDev()) console.debug('[auth-session]', ...args);
}

function resolveLimitMs(defaultLimitMs) {
  if (!isDev()) return defaultLimitMs;
  try {
    const raw = localStorage.getItem(DEBUG_OVERRIDE_KEY);
    const override = Number(raw);
    if (Number.isFinite(override) && override > 0) return override;
  } catch {
    // ignore
  }
  return defaultLimitMs;
}

function readPersistedLastActivity() {
  try {
    const raw = Number(sessionStorage.getItem(LAST_ACTIVITY_STORAGE_KEY));
    if (Number.isFinite(raw) && raw > 0) return raw;
  } catch {
    // sessionStorage unavailable (private mode edge cases) — fall through
  }
  return Date.now();
}

/**
 * P0 fix (2026-07-04): a genuinely fresh, interactive sign-in must never be
 * treated as "idle". Before this fix, `ss_last_activity_at` in
 * sessionStorage can survive far longer than intended — sessionStorage is
 * restored by "continue where you left off" browser session-restore across
 * a full restart, or simply persists for as long as this same tab has been
 * open since an earlier login — so a brand-new sign-in could immediately
 * read back an ancient timestamp, compute idleForMs > the 4-hour limit, and
 * trigger an instant auto sign-out that raced with (and broke) the
 * post-login Firestore profile read. Callers MUST invoke this immediately
 * after a successful interactive sign-in (see App.jsx SessionGuard), before
 * this hook's own mount effect gets a chance to read the stale value.
 */
export function resetInactivityClock() {
  try {
    sessionStorage.setItem(LAST_ACTIVITY_STORAGE_KEY, String(Date.now()));
  } catch {
    // ignore — worst case the mount-time check below re-derives Date.now()
  }
}

/**
 * Mounts a single inactivity guard for the currently signed-in user.
 * Does nothing while `currentUser` is falsy.
 *
 * @param {import('firebase/auth').User|null|undefined} currentUser
 * @param {object} [options]
 * @param {number} [options.limitMs]      Override the inactivity limit (mainly for tests/QA).
 * @param {number} [options.warnBeforeMs] If > 0, calls onWarn this many ms before the deadline.
 * @param {(msRemaining: number) => void} [options.onWarn]
 * @param {() => void} [options.onResetWarn] Called when activity resumes after a warning fired.
 */
export function useInactivityLogout(currentUser, options = {}) {
  const optsRef = useRef(options);
  optsRef.current = options;

  const lastActivityRef = useRef(null);
  const lastPersistRef  = useRef(0);
  const warnedRef       = useRef(false);

  const persistLastActivity = useCallback((ts) => {
    try { sessionStorage.setItem(LAST_ACTIVITY_STORAGE_KEY, String(ts)); } catch { /* ignore */ }
  }, []);

  const recordActivity = useCallback(() => {
    const now = Date.now();
    lastActivityRef.current = now;
    if (now - lastPersistRef.current >= PERSIST_THROTTLE_MS) {
      lastPersistRef.current = now;
      persistLastActivity(now);
    }
    if (warnedRef.current) {
      warnedRef.current = false;
      optsRef.current.onResetWarn?.();
    }
  }, [persistLastActivity]);

  const performTimeoutLogout = useCallback(async () => {
    devLog('inactivity timeout triggered — signing out');

    try { sessionStorage.setItem(LOGOUT_REASON_STORAGE_KEY, 'inactive'); } catch { /* ignore */ }

    try {
      if (typeof BroadcastChannel !== 'undefined') {
        const channel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
        channel.postMessage({ type: 'FORCE_LOGOUT', reason: 'inactive' });
        channel.close();
      } else {
        localStorage.setItem(STORAGE_FORCE_LOGOUT_KEY, String(Date.now()));
      }
    } catch { /* ignore — cross-tab broadcast is best-effort */ }

    try { await signOut(auth); } catch { /* already signed out */ }
  }, []);

  const checkTimeout = useCallback(() => {
    if (lastActivityRef.current === null) return;
    const { onWarn, warnBeforeMs = 0 } = optsRef.current;
    const limitMs = resolveLimitMs(optsRef.current.limitMs || INACTIVITY_LIMIT_MS);
    const idleForMs = Date.now() - lastActivityRef.current;

    if (idleForMs >= limitMs) {
      console.warn('[SESSION] Auto-logout triggered', {
        uid: auth.currentUser?.uid ?? null,
        reason: 'inactive',
        lastActivity: lastActivityRef.current,
        ageMs: idleForMs,
        path: typeof window !== 'undefined' ? window.location.pathname : null,
      });
      performTimeoutLogout();
      return;
    }
    if (warnBeforeMs > 0 && !warnedRef.current && idleForMs >= limitMs - warnBeforeMs) {
      warnedRef.current = true;
      onWarn?.(limitMs - idleForMs);
    }
  }, [performTimeoutLogout]);

  useEffect(() => {
    if (!currentUser) return undefined;

    lastActivityRef.current = readPersistedLastActivity();
    warnedRef.current = false;

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') checkTimeout();
    };
    const handleRecheck = () => checkTimeout();

    ACTIVITY_EVENTS.forEach(evt => window.addEventListener(evt, recordActivity, { passive: true }));
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', handleRecheck);
    window.addEventListener('pageshow', handleRecheck);

    // Run an immediate check on mount too, in case sessionStorage already
    // shows the limit was exceeded while this tab was reloading.
    checkTimeout();
    const interval = setInterval(checkTimeout, CHECK_INTERVAL_MS);

    // ── Cross-tab logout ────────────────────────────────────────────────
    const handleForceLogout = () => {
      devLog('received cross-tab FORCE_LOGOUT — signing out this tab too');
      try { sessionStorage.setItem(LOGOUT_REASON_STORAGE_KEY, 'inactive'); } catch { /* ignore */ }
      signOut(auth).catch(() => {});
    };

    let channel = null;
    if (typeof BroadcastChannel !== 'undefined') {
      channel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
      channel.onmessage = (event) => {
        if (event?.data?.type === 'FORCE_LOGOUT') handleForceLogout();
      };
    }
    const handleStorage = (e) => {
      if (e.key === STORAGE_FORCE_LOGOUT_KEY && e.newValue) handleForceLogout();
    };
    window.addEventListener('storage', handleStorage);

    return () => {
      ACTIVITY_EVENTS.forEach(evt => window.removeEventListener(evt, recordActivity));
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', handleRecheck);
      window.removeEventListener('pageshow', handleRecheck);
      window.removeEventListener('storage', handleStorage);
      clearInterval(interval);
      if (channel) channel.close();
    };
  }, [currentUser, recordActivity, checkTimeout]);
}

export default useInactivityLogout;
